import { doorPreviewColour } from '@planner/config';
import { useRouterState } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';
import { useDoorStore } from '../stores/doorStore.js';
import { plannerLanded } from '../lib/analytics/planner.js';
import { track } from '../lib/analytics/index.js';

let reportedRestore: string | null = null;
useDoorStore.subscribe((state, previous) => {
  if (state.restoreNotice !== previous.restoreNotice) reportedRestore = null;
});

/**
 * Saved-progress notices shown above every planner Step. They live in the
 * planner layout so they stay visible after a Step navigates onwards.
 */
export function PlannerNotices() {
  const persistence = useDoorStore((state) => state.persistence);
  const restoreNotice = useDoorStore((state) => state.restoreNotice);
  const guardNotice = useDoorStore((state) => state.guardNotice);
  const dismissRestoreNotice = useDoorStore((state) => state.dismissRestoreNotice);
  const setGuardNotice = useDoorStore((state) => state.setGuardNotice);
  const path = useRouterState({
    select: (state) => state.location.pathname.toLowerCase().replace(/\/+$/, '') || '/',
  });
  const target =
    guardNotice === 'door-type'
      ? '/planner/type'
      : guardNotice === 'lock'
        ? '/planner/lock'
        : '/planner/measure';
  const showGuardNotice = guardNotice && path === target;
  const previousPath = useRef(path);

  useEffect(() => {
    plannerLanded(path);
  }, [path]);
  useEffect(() => {
    if (restoreNotice && restoreNotice !== reportedRestore) {
      track(
        'error_occurred',
        restoreNotice === 'invalid'
          ? { context: 'draft_discarded', message: 'Saved draft was discarded' }
          : { context: 'draft_recovered', message: 'Saved draft was repaired' },
      );
    }
    reportedRestore = restoreNotice;
  }, [restoreNotice]);

  useEffect(() => {
    const leftTarget = previousPath.current === target && path !== target;
    previousPath.current = path;
    if (guardNotice && leftTarget) {
      setGuardNotice(null);
    }
  }, [guardNotice, path, setGuardNotice, target]);

  if (persistence === 'ok' && !restoreNotice && !showGuardNotice) {
    return null;
  }
  return (
    <div className="mx-auto w-full max-w-5xl space-y-2 px-4 pt-4">
      {persistence === 'unavailable' && (
        // Not dismissible: the condition lasts for the whole visit.
        <p
          role="status"
          data-name="planner-storage-warning"
          className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900"
        >
          Your browser isn&apos;t letting us save your progress. You can keep going, but your
          choices will be lost if you refresh or close this page.
        </p>
      )}
      {restoreNotice && (
        <div
          role="alert"
          data-name="planner-restore-notice"
          className="flex items-start justify-between gap-4 rounded-md border border-gray-300 bg-white px-4 py-3 text-sm"
        >
          <p>
            {restoreNotice === 'unknown-colour'
              ? `Your saved frame colour is no longer offered. It has been reset to ${doorPreviewColour}; your other choices have been kept.`
              : restoreNotice === 'invalid-cart'
                ? 'Your saved Cart reference could not be restored. Your door choices have been kept.'
                : "We couldn't restore your previous progress. Please check your door type and measurements."}
          </p>
          <button
            type="button"
            data-name="planner-restore-notice-dismiss"
            onClick={dismissRestoreNotice}
            className="shrink-0 font-semibold underline hover:no-underline focus-visible:outline-2 focus-visible:outline-brand"
          >
            Dismiss
          </button>
        </div>
      )}
      {showGuardNotice && (
        <div
          role="alert"
          data-name="planner-navigation-notice"
          className="flex items-start justify-between gap-4 rounded-md border border-gray-300 bg-white px-4 py-3 text-sm"
        >
          <p>
            {guardNotice === 'door-type'
              ? 'Choose a door type before continuing to Lock & Handle.'
              : guardNotice === 'lock'
                ? 'Complete your handle height and side before continuing.'
                : guardNotice === 'measurements'
                  ? 'Complete your measurements and acknowledgements before continuing to Lock & Handle.'
                  : 'We could not verify this size. Check the catalogue and try again.'}
          </p>
          <button
            type="button"
            data-name="planner-navigation-notice-dismiss"
            onClick={() => setGuardNotice(null)}
            className="shrink-0 font-semibold underline hover:no-underline focus-visible:outline-2 focus-visible:outline-brand"
          >
            Dismiss
          </button>
        </div>
      )}
    </div>
  );
}
